import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 220;
const MARGIN = { top: 20, right: 20, bottom: 35, left: 50 };
const NUM_BINS = 15;

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))',
    gap: '12px',
    marginTop: '16px',
    padding: '16px',
    background: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
  statItem: {
    textAlign: 'center',
  },
  statLabel: {
    fontSize: '10px',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '2px',
  },
  statValue: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
  },
  legendRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '8px',
    fontSize: '11px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
};

export default function PriceRangeSlider({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  useEffect(() => {
    if (!data || !data.values || data.values.length === 0 || !data.stats) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(400, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg
      .attr('viewBox', `0 0 ${width} ${CHART_HEIGHT}`)
      .attr('width', '100%')
      .attr('height', CHART_HEIGHT)
      .style('overflow', 'visible');

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const values = data.values.filter((v) => v != null && typeof v === 'number');
    if (values.length === 0) return;

    const { stats } = data;

    // X scale
    const xExtent = d3.extent(values);
    const xPadding = (xExtent[1] - xExtent[0]) * 0.05 || 1;
    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPadding, xExtent[1] + xPadding])
      .nice()
      .range([0, innerW]);

    // Histogram bins
    const histogramGenerator = d3.bin().domain(x.domain()).thresholds(NUM_BINS);
    const bins = histogramGenerator(values);

    // Y scale
    const maxCount = d3.max(bins, (d) => d.length);
    const y = d3.scaleLinear()
      .domain([0, maxCount])
      .nice()
      .range([innerH, 0]);

    // Horizontal grid lines
    const yTicks = y.ticks(5);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Histogram bars with gradient effect
    bins.forEach((bin, i) => {
      const barHeight = innerH - y(bin.length);
      if (barHeight <= 0) return;

      const barX = x(bin.x0) + 1;
      const barW = Math.max(0, x(bin.x1) - x(bin.x0) - 2);
      const opacity = 0.5 + (bin.length / maxCount) * 0.4;

      g.append('rect')
        .attr('x', barX)
        .attr('y', y(bin.length))
        .attr('width', barW)
        .attr('height', barHeight)
        .attr('fill', '#2563eb')
        .attr('opacity', opacity)
        .attr('rx', 1);
    });

    // Reference lines

    // P25 (dotted gray)
    if (stats.p25 != null) {
      g.append('line')
        .attr('x1', x(stats.p25))
        .attr('x2', x(stats.p25))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#9ca3af')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2 3');
    }

    // P75 (dotted gray)
    if (stats.p75 != null) {
      g.append('line')
        .attr('x1', x(stats.p75))
        .attr('x2', x(stats.p75))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#9ca3af')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '2 3');
    }

    // Median (dashed green)
    if (stats.median != null) {
      g.append('line')
        .attr('x1', x(stats.median))
        .attr('x2', x(stats.median))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#16a34a')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '6 3');

      g.append('text')
        .attr('x', x(stats.median) + 4)
        .attr('y', 10)
        .attr('font-size', '9px')
        .attr('fill', '#16a34a')
        .text(`Mdn=$${stats.median.toFixed(2)}`);
    }

    // Mean (solid blue)
    if (stats.mean != null) {
      g.append('line')
        .attr('x1', x(stats.mean))
        .attr('x2', x(stats.mean))
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 1.5);

      g.append('text')
        .attr('x', x(stats.mean) + 4)
        .attr('y', 22)
        .attr('font-size', '9px')
        .attr('fill', '#2563eb')
        .text(`\u03BC=$${stats.mean.toFixed(2)}`);
    }

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(
        d3.axisBottom(x)
          .ticks(8)
          .tickSize(0)
          .tickFormat((v) => `$${v}`)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');

    // Y axis
    g.append('g')
      .call(
        d3.axisLeft(y)
          .ticks(5)
          .tickSize(0)
          .tickFormat((v) => v)
      )
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.domain').attr('stroke', '#e5e7eb');
    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -38)
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', '#9ca3af')
      .text('Frequency');

    // Invisible overlay rectangles for tooltip hover
    bins.forEach((bin) => {
      const bx0 = x(bin.x0);
      const bx1 = x(bin.x1);
      const bw = bx1 - bx0;

      g.append('rect')
        .attr('x', bx0)
        .attr('y', 0)
        .attr('width', bw)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            x0: bin.x0,
            x1: bin.x1,
            count: bin.length,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // Empty state
  if (!data || !data.values || data.values.length === 0 || !data.stats) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Distribution Overview</div>
          <div style={styles.subtitle}>
            Histogram of current pest365 prices across all addresses
          </div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No price distribution data available
        </div>
      </div>
    );
  }

  const { stats } = data;

  const statItems = [
    { label: 'Count', value: stats.count },
    { label: 'Mean', value: `$${stats.mean.toFixed(2)}` },
    { label: 'Median', value: `$${stats.median.toFixed(2)}` },
    { label: 'Min', value: `$${stats.min.toFixed(2)}` },
    { label: 'Max', value: `$${stats.max.toFixed(2)}` },
    { label: 'Std Dev', value: `$${stats.stdDev.toFixed(2)}` },
    { label: 'P25', value: `$${stats.p25.toFixed(2)}` },
    { label: 'P75', value: `$${stats.p75.toFixed(2)}` },
    { label: 'IQR', value: `$${stats.iqr.toFixed(2)}` },
  ];

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Distribution Overview</div>
        <div style={styles.subtitle}>
          Histogram of current pest365 prices across all addresses
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Legend */}
      <div style={styles.legendRow}>
        <div style={styles.legendItem}>
          <span style={{ width: '16px', height: '2px', background: '#2563eb', display: 'inline-block' }} />
          <span>Mean</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ width: '16px', height: '0', borderTop: '2px dashed #16a34a', display: 'inline-block' }} />
          <span>Median</span>
        </div>
        <div style={styles.legendItem}>
          <span style={{ width: '16px', height: '0', borderTop: '2px dotted #9ca3af', display: 'inline-block' }} />
          <span>P25 / P75</span>
        </div>
      </div>

      {/* Stats panel */}
      <div style={styles.statsGrid}>
        {statItems.map((item) => (
          <div key={item.label} style={styles.statItem}>
            <div style={styles.statLabel}>{item.label}</div>
            <div style={styles.statValue}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '2px' }}>
            ${tooltip.x0.toFixed(2)} &ndash; ${tooltip.x1.toFixed(2)}
          </div>
          <div style={{ color: '#2563eb', fontWeight: 600 }}>
            {tooltip.count} address{tooltip.count !== 1 ? 'es' : ''}
          </div>
        </div>
      )}
    </div>
  );
}
